// Format currency
export const formatCurrency = (amount: number, currency = "INR") => {
  if (currency === "INR") {
    // Use the rupee symbol for INR
    return `₹${new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(amount)}`;
  } else {
    // For other currencies, use the standard format
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(amount);
  }
};

// Format date
export const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Sample data for loans
export const loansData = [
  { 
    id: 1, 
    instrumentType: "Term Loan",
    lender: "HDFC Bank",
    amount: 500000000,
    currency: "INR",
    interestRate: 8.75,
    interestType: "Floating",
    sanctionDate: "2024-06-15",
    maturityDate: "2029-06-15",
    repaymentType: "Equated Principal",
    status: "Active",
    documents: [
      { name: "Sanction Letter", date: "2024-06-10" },
      { name: "Master Facility Agreement", date: "2024-06-15" },
      { name: "Deed of Hypothecation", date: "2024-06-15" }
    ],
    covenants: [
      { 
        type: "Financial", 
        name: "Debt-Equity Ratio", 
        threshold: "≤ 2.5x",
        criticality: "High",
        status: "Compliant"
      },
      { 
        type: "Financial", 
        name: "Interest Coverage Ratio", 
        threshold: "≥ 1.5x",
        criticality: "High",
        status: "Compliant"
      },
      { 
        type: "Non-Financial", 
        name: "Quarterly Compliance Certificate",
        threshold: "Within 45 days of quarter end",
        criticality: "Medium",
        status: "Compliant"
      }
    ],
    repayments: [
      { date: "2024-09-15", principal: 25000000, interest: 10937500, total: 35937500, status: "Scheduled" },
      { date: "2024-12-15", principal: 25000000, interest: 10390625, total: 35390625, status: "Scheduled" },
      { date: "2025-03-15", principal: 25000000, interest: 9843750, total: 34843750, status: "Scheduled" }
    ]
  },
  { 
    id: 2, 
    instrumentType: "Non-Convertible Debenture",
    lender: "Franklin Templeton MF",
    amount: 500000000,
    currency: "INR",
    interestRate: 9.25,
    interestType: "Fixed",
    sanctionDate: "2024-02-20",
    maturityDate: "2029-02-20",
    repaymentType: "Interest Only, Principal at Maturity",
    status: "Active",
    documents: [
      { name: "Information Memorandum", date: "2024-02-05" },
      { name: "Debenture Trust Deed", date: "2024-02-20" }
    ],
    covenants: [
      { 
        type: "Financial", 
        name: "Net Worth", 
        threshold: "≥ ₹8 billion",
        criticality: "High",
        status: "Compliant"
      },
      { 
        type: "Non-Financial", 
        name: "Credit Rating",
        threshold: "Maintain minimum AA-",
        criticality: "High",
        status: "Compliant"
      }
    ],
    repayments: [
      { date: "2024-08-20", principal: 0, interest: 23125000, total: 23125000, status: "Scheduled" },
      { date: "2025-02-20", principal: 0, interest: 23125000, total: 23125000, status: "Scheduled" }
    ]
  },
  { 
    id: 3, 
    instrumentType: "Working Capital Loan",
    lender: "State Bank of India",
    amount: 300000000,
    currency: "INR",
    interestRate: 8.5,
    interestType: "Floating",
    sanctionDate: "2024-04-10",
    maturityDate: "2025-04-10",
    repaymentType: "Interest Monthly, Principal at Maturity",
    status: "Active",
    documents: [
      { name: "Sanction Letter", date: "2024-04-05" },
      { name: "Working Capital Agreement", date: "2024-04-10" }
    ],
    covenants: [
      { 
        type: "Financial", 
        name: "Current Ratio", 
        threshold: "≥ 1.0x",
        criticality: "Medium",
        status: "Compliant"
      }
    ],
    repayments: [
      { date: "2024-05-10", principal: 0, interest: 2125000, total: 2125000, status: "Paid" },
      { date: "2024-06-10", principal: 0, interest: 2125000, total: 2125000, status: "Paid" },
      { date: "2024-07-10", principal: 0, interest: 2125000, total: 2125000, status: "Scheduled" }
    ]
  },
  { 
    id: 4, 
    instrumentType: "Term Loan",
    lender: "Aditya Birla Capital",
    amount: 350000000,
    currency: "INR",
    interestRate: 10.25,
    interestType: "Fixed",
    sanctionDate: "2022-11-15",
    maturityDate: "2025-11-15",
    repaymentType: "EMI",
    status: "Active",
    documents: [
      { name: "Sanction Letter", date: "2022-11-10" },
      { name: "Loan Agreement", date: "2022-11-15" }
    ],
    covenants: [
      { 
        type: "Financial", 
        name: "Debt Service Coverage Ratio", 
        threshold: "≥ 1.2x",
        criticality: "High",
        status: "Compliant"
      }
    ],
    repayments: [
      { date: "2024-05-15", principal: 9722222, interest: 2994792, total: 12717014, status: "Paid" },
      { date: "2024-06-15", principal: 9722222, interest: 2910764, total: 12632986, status: "Paid" },
      { date: "2024-07-15", principal: 9722222, interest: 2826736, total: 12548958, status: "Scheduled" }
    ]
  },
  { 
    id: 5, 
    instrumentType: "External Commercial Borrowing",
    lender: "International Finance Corp",
    amount: 10000000,
    currency: "USD",
    interestRate: 4.25,
    interestType: "Floating (SOFR + 250 bps)",
    sanctionDate: "2023-08-20",
    maturityDate: "2028-08-20",
    repaymentType: "Semi-annual Principal and Interest",
    status: "Active",
    documents: [
      { name: "Facility Agreement", date: "2023-08-15" }
    ],
    covenants: [
      { 
        type: "Financial", 
        name: "Capital Adequacy Ratio", 
        threshold: "≥ 15%",
        criticality: "Critical",
        status: "Compliant"
      }
    ],
    repayments: [
      { date: "2024-02-20", principal: 1000000, interest: 212500, total: 1212500, status: "Paid" },
      { date: "2024-08-20", principal: 1000000, interest: 191250, total: 1191250, status: "Scheduled" }
    ]
  },
  // Past loans
  { 
    id: 6, 
    instrumentType: "Term Loan",
    lender: "ICICI Bank",
    amount: 200000000,
    currency: "INR",
    interestRate: 9.5,
    interestType: "Fixed",
    sanctionDate: "2019-05-10",
    maturityDate: "2024-05-10",
    repaymentType: "Equated Principal",
    status: "Closed",
    documents: [
      { name: "Sanction Letter", date: "2019-05-05" },
      { name: "Loan Agreement", date: "2019-05-10" },
      { name: "Closure Certificate", date: "2024-05-10" }
    ],
    covenants: [
      { 
        type: "Financial", 
        name: "Debt-Equity Ratio", 
        threshold: "≤ 3.0x",
        criticality: "High",
        status: "Compliant"
      }
    ],
    repayments: [
      { date: "2023-11-10", principal: 12500000, interest: 1187500, total: 13687500, status: "Paid" },
      { date: "2024-02-10", principal: 12500000, interest: 890625, total: 13390625, status: "Paid" },
      { date: "2024-05-10", principal: 12500000, interest: 593750, total: 13093750, status: "Paid" }
    ]
  },
  { 
    id: 7, 
    instrumentType: "Commercial Paper",
    lender: "Mutual Fund Consortium",
    amount: 250000000,
    currency: "INR",
    interestRate: 7.25,
    interestType: "Discount to Face Value",
    sanctionDate: "2023-07-15",
    maturityDate: "2024-01-15",
    repaymentType: "Bullet Payment",
    status: "Closed",
    documents: [
      { name: "Information Memorandum", date: "2023-07-10" },
      { name: "ISIN Allotment", date: "2023-07-15" },
      { name: "Redemption Confirmation", date: "2024-01-15" }
    ],
    covenants: [],
    repayments: [
      { date: "2024-01-15", principal: 250000000, interest: 9062500, total: 259062500, status: "Paid" }
    ]
  }
];
